import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import PageHeader from 'components/common/PageHeader';
import { createMlTestUser, deleteMlTestUser, fetchDevTestingSummary } from 'helpers/devTesting';
import { useAuth } from 'context/AuthContext';


const EMPTY_SUMMARY = {
  meta: {
    shared_ml_account: null,
    notes: {}
  },
  test_users: [],
  test_cards: {
    docs_url: '',
    cards: [],
    scenarios: []
  },
  docs: {}
};


const DevTesting = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creatingUser, setCreatingUser] = useState(false);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [error, setError] = useState('');
  const [createdUser, setCreatedUser] = useState(null);

  const canCreate = hasPermission('dev_testing.create');
  const canDelete = hasPermission('dev_testing.delete');

  const loadSummary = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchDevTestingSummary();
      setSummary(response);
    } catch (requestError) {
      console.error(requestError);
      setError(requestError.response?.data?.error || t('DEV_TESTING.ERRORS.LOAD'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const handleCreateTestUser = async () => {
    setCreatingUser(true);
    try {
      const response = await createMlTestUser();
      setSummary(currentState => ({
        ...currentState,
        test_users: [response.test_user, ...(currentState.test_users || [])]
      }));
      setCreatedUser(response.test_user);
      toast.success(t('DEV_TESTING.TEST_USERS.CREATE_SUCCESS'), { theme: 'colored' });
    } catch (requestError) {
      console.error(requestError);
      toast.error(requestError.response?.data?.error || t('DEV_TESTING.ERRORS.CREATE'), {
        theme: 'colored'
      });
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteTestUser = async testUserId => {
    try {
      await deleteMlTestUser(testUserId);
      setSummary(currentState => ({
        ...currentState,
        test_users: (currentState.test_users || []).filter(user => user.id !== testUserId)
      }));
      toast.success(t('DEV_TESTING.TEST_USERS.DELETE_SUCCESS'), { theme: 'colored' });
    } catch (requestError) {
      console.error(requestError);
      toast.error(requestError.response?.data?.error || t('DEV_TESTING.ERRORS.DELETE'), {
        theme: 'colored'
      });
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-6">
        <Spinner animation="border" />
      </div>
    );
  }

  const sharedAccount = summary.meta?.shared_ml_account || {};

  return (
    <>
      <PageHeader
        title={t('DEV_TESTING.PAGE_TITLE')}
        description={t('DEV_TESTING.PAGE_DESCRIPTION')}
        className="mb-3"
      >
        {canCreate ? (
          <div className="mt-3">
            <Button onClick={handleCreateTestUser} disabled={creatingUser}>
              {creatingUser ? t('DEV_TESTING.TEST_USERS.CREATING') : t('DEV_TESTING.TEST_USERS.CREATE_ACTION')}
            </Button>
          </div>
        ) : null}
      </PageHeader>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3 mb-3">
        <Col xl={7}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-1">{t('DEV_TESTING.TEST_USERS.TITLE')}</h5>
              <p className="text-600 mb-0">{t('DEV_TESTING.TEST_USERS.DESCRIPTION')}</p>
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                <strong>{t('DEV_TESTING.NOTES.PLATFORM_USERS_TITLE')}</strong> {t('DEV_TESTING.NOTES.PLATFORM_USERS')}
              </Alert>

              <div className="mb-3 d-flex flex-wrap gap-3">
                <div>
                  <div className="text-600 fs--2 text-uppercase">{t('DEV_TESTING.SHARED_ACCOUNT')}</div>
                  <strong>{sharedAccount.nickname || t('DEV_TESTING.NOT_AVAILABLE')}</strong>
                </div>
                <div>
                  <div className="text-600 fs--2 text-uppercase">{t('DEV_TESTING.SITE')}</div>
                  <strong>{sharedAccount.site_id || t('DEV_TESTING.NOT_AVAILABLE')}</strong>
                </div>
              </div>

              <Table responsive hover>
                <thead>
                  <tr>
                    <th>{t('DEV_TESTING.TEST_USERS.TABLE.NICKNAME')}</th>
                    <th>{t('DEV_TESTING.TEST_USERS.TABLE.USER_ID')}</th>
                    <th>{t('DEV_TESTING.TEST_USERS.TABLE.PASSWORD')}</th>
                    <th>{t('DEV_TESTING.TEST_USERS.TABLE.STATUS')}</th>
                    <th className="text-end">{t('DEV_TESTING.TEST_USERS.TABLE.ACTIONS')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary.test_users || []).map(testUser => (
                    <tr key={testUser.id}>
                      <td>{testUser.nickname}</td>
                      <td>{testUser.ml_user_id}</td>
                      <td><code>{testUser.password}</code></td>
                      <td>
                        <Badge bg="light" text="dark">{testUser.status || t('DEV_TESTING.NOT_AVAILABLE')}</Badge>
                      </td>
                      <td className="text-end">
                        {canDelete ? (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteTestUser(testUser.id)}
                          >
                            {t('DEV_TESTING.TEST_USERS.DELETE_ACTION')}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={5}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-1">{t('DEV_TESTING.TEST_CARDS.TITLE')}</h5>
              <p className="text-600 mb-0">{t('DEV_TESTING.TEST_CARDS.DESCRIPTION')}</p>
            </Card.Header>
            <Card.Body className="d-flex flex-column gap-3">
              <div>
                <Button
                  variant="outline-primary"
                  href={summary.test_cards?.docs_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('DEV_TESTING.TEST_CARDS.OPEN_DOCS')}
                </Button>
              </div>

              {(summary.test_cards?.cards || []).map(card => (
                <Card className="border" key={`${card.type}-${card.brand}-${card.number}`}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{card.brand}</strong>
                      <Badge bg="light" text="dark">{card.type}</Badge>
                    </div>
                    <div><code>{card.number}</code></div>
                    <div className="text-600 fs--1 mt-2">
                      CVV: {card.security_code} | Exp: {card.expiration}
                    </div>
                  </Card.Body>
                </Card>
              ))}

              <Card className="border">
                <Card.Body>
                  <h6 className="mb-2">{t('DEV_TESTING.TEST_CARDS.SCENARIOS_TITLE')}</h6>
                  <div className="d-flex flex-column gap-2">
                    {(summary.test_cards?.scenarios || []).map(scenario => (
                      <div key={scenario.status}>
                        <strong>{scenario.cardholder_name}</strong> - {t(`DEV_TESTING.TEST_CARDS.SCENARIOS.${scenario.status}`)}
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={Boolean(createdUser)} onHide={() => setCreatedUser(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('DEV_TESTING.TEST_USERS.CREATED_MODAL_TITLE')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('DEV_TESTING.TEST_USERS.CREATED_MODAL_DESCRIPTION')}</p>
          {createdUser ? (
            <div className="d-flex flex-column gap-2">
              <div><strong>{t('DEV_TESTING.TEST_USERS.TABLE.NICKNAME')}:</strong> <code>{createdUser.nickname}</code></div>
              <div><strong>{t('DEV_TESTING.TEST_USERS.TABLE.PASSWORD')}:</strong> <code>{createdUser.password}</code></div>
              <div><strong>{t('DEV_TESTING.TEST_USERS.TABLE.USER_ID')}:</strong> {createdUser.ml_user_id}</div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setCreatedUser(null)}>
            {t('DEV_TESTING.CLOSE')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};


export default DevTesting;
